import { Position } from '@vue-flow/core'
import type { Edge, GraphNode, HandleElement } from '@vue-flow/core'
import type { InjectionKey } from 'vue'
import type { PresencePeer, ResolvedComment } from '@rialtic/nuxt-shared'
import { fieldIdFromCommentTarget, nodeItemId } from '@worker/policy-builder/client'
import type {
  NodePosition,
  PolicyDocView,
  PolicyInsight,
  PolicyInsightType,
  PredicateData,
  TreeVersion,
} from '@worker/policy-builder/client'
import {
  layoutPredicateTree,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '~/utils/policy-studio/predicateTree'

export interface DecisionTree {
  nodes: PredicateData[]
  insights: PolicyInsight[]
}

export type NodeMove = { id: string; position: NodePosition }

/** A tree node as the room holds it: a decision or an outcome. */
export type TreeEntity =
  | { kind: 'predicate'; node: PredicateData }
  | { kind: 'insight'; node: PolicyInsight }

/** What one collaborative-tree node renders: the entity, where it sits, and who is on it. */
export type CollabTreeNodeData = TreeEntity & {
  direction: 'LR' | 'TB'
  root: boolean
  orphan: boolean
  selected: boolean
  connectable: boolean
  viewers: PresencePeer[]
  counts: { fields: number; discussion: number }
}

/* Pure edits to a decision tree. Yes/no paths point at a node's *label*, as
   the engine export and `validateTree` read them, so renaming, removing and
   connecting all keep every path that names a node in step with it. */

export type YesNo = 'yes' | 'no'
const PATH_KEY = { yes: 'yesPath', no: 'noPath' } as const

/** Vue Flow handle id → which path it sets. */
export const handlePath = (handle: string | null | undefined): YesNo | null =>
  handle === 'predicate-node-yes-path'
    ? 'yes'
    : handle === 'predicate-node-no-path'
      ? 'no'
      : null

const labelOf = (tree: DecisionTree, id: string): string | undefined =>
  [...tree.nodes, ...tree.insights].find((entry) => entry.id === id)?.label

const repoint = (
  nodes: PredicateData[],
  from: string,
  to: string,
): PredicateData[] =>
  nodes.map((node) => ({
    ...node,
    yesPath: node.yesPath === from ? to : node.yesPath,
    noPath: node.noPath === from ? to : node.noPath,
  }))

/** Relabels a node or outcome and every path that named it. */
export const renameNode = (
  tree: DecisionTree,
  id: string,
  label: string,
): DecisionTree => {
  const previous = labelOf(tree, id)
  if (previous === undefined || previous === label) return tree
  const relabel = <T extends { id: string; label: string }>(entry: T): T =>
    entry.id === id ? { ...entry, label } : entry
  /* An empty old label names nothing, so no path followed it. */
  const nodes = previous ? repoint(tree.nodes, previous, label) : tree.nodes
  return { nodes: nodes.map(relabel), insights: tree.insights.map(relabel) }
}

/** Points `sourceId`'s yes or no path at `targetId`. A node never points at itself. */
export const connectNodes = (
  tree: DecisionTree,
  sourceId: string,
  path: YesNo,
  targetId: string,
): DecisionTree => {
  const label = labelOf(tree, targetId)
  if (!label || sourceId === targetId) return tree
  return {
    ...tree,
    nodes: tree.nodes.map((node) =>
      node.id === sourceId ? { ...node, [PATH_KEY[path]]: label } : node,
    ),
  }
}

export const disconnectPath = (
  tree: DecisionTree,
  sourceId: string,
  path: YesNo,
): DecisionTree => ({
  ...tree,
  nodes: tree.nodes.map((node) =>
    node.id === sourceId ? { ...node, [PATH_KEY[path]]: '' } : node,
  ),
})

/** Moves one path to another handle or target: dragging either end of an
 *  edge. A move onto the path's own node, or onto nothing, leaves the tree. */
export const reconnectPath = (
  tree: DecisionTree,
  from: { source: string; path: YesNo },
  to: { source: string; path: YesNo; target: string },
): DecisionTree =>
  to.source === to.target || !labelOf(tree, to.target)
    ? tree
    : connectNodes(
        disconnectPath(tree, from.source, from.path),
        to.source,
        to.path,
        to.target,
      )

/** Drops a node or outcome and clears every path that led to it. */
export const removeFromTree = (
  tree: DecisionTree,
  id: string,
): DecisionTree => {
  const label = labelOf(tree, id)
  const nodes = tree.nodes.filter((node) => node.id !== id)
  return {
    nodes: label ? repoint(nodes, label, '') : nodes,
    insights: tree.insights.filter((insight) => insight.id !== id),
  }
}

/** The next free hundred for a decision (100, 200, …), the builder's numbering. */
const nextDecisionLabel = (tree: DecisionTree): string => {
  const used = tree.nodes
    .map((node) => Number.parseInt(node.label, 10))
    .filter(Number.isFinite)
  return String((Math.floor(Math.max(0, ...used) / 100) + 1) * 100)
}

export const addDecision = (tree: DecisionTree, id: string): DecisionTree => ({
  ...tree,
  nodes: [
    ...tree.nodes,
    {
      id,
      label: nextDecisionLabel(tree),
      predicate: '',
      yesPath: '',
      noPath: '',
    },
  ],
})

/** Builds the one tree edit that places a blank decision at a dropped
 *  flow-space point and attaches its source path. Passing `id` keeps callers
 *  and tests deterministic; canvas drops use the generated id. */
export const addDecisionOnPath = (
  from: { source: string; path: YesNo },
  centre: NodePosition,
  id = crypto.randomUUID(),
): { change: (tree: DecisionTree) => DecisionTree; move: NodeMove } => ({
  change: (tree) =>
    connectNodes(addDecision(tree, id), from.source, from.path, id),
  move: {
    id,
    position: {
      x: centre.x - NODE_WIDTH / 2,
      y: centre.y - NODE_HEIGHT / 2,
    },
  },
})

export const addOutcome = (
  tree: DecisionTree,
  id: string,
  insightType?: PolicyInsightType,
): DecisionTree => {
  const labels = new Set(tree.insights.map((insight) => insight.label))
  let n = tree.insights.length + 1
  while (labels.has(`Outcome ${n}`)) n++
  return {
    ...tree,
    insights: [
      ...tree.insights,
      { id, label: `Outcome ${n}`, insightText: '', insightType },
    ],
  }
}

/** The four outcome types as the tree offers them: short names, in the
 *  order a designer reaches for them, tinted like the outcome cards. */
export const OUTCOME_TYPES = [
  {
    value: 'Claim Line Not Payable',
    label: 'Not payable',
    icon: 'i-ph-prohibit',
    color: 'error',
  },
  {
    value: 'Claim Line Partially Payable',
    label: 'Partially payable',
    icon: 'i-ph-circle-half',
    color: 'error',
  },
  {
    value: 'Claim Line Valid',
    label: 'Valid',
    icon: 'i-ph-check-circle',
    color: 'success',
  },
  {
    value: 'Not Applicable',
    label: 'Not applicable',
    icon: 'i-ph-minus-circle',
    color: 'warning',
  },
] as const satisfies readonly {
  value: PolicyInsightType
  label: string
  icon: string
  color: 'error' | 'success' | 'warning'
}[]

/** Lets an outcome card on the canvas set its own type in one click. */
export const SET_OUTCOME_TYPE: InjectionKey<
  (id: string, insightType: PolicyInsightType) => void
> = Symbol('setOutcomeType')

/** Who has each node selected, first-come per node (their colour rings it). */
export const viewersByNode = (
  peers: PresencePeer[],
): Map<string, PresencePeer[]> => {
  const byNode = new Map<string, PresencePeer[]>()
  for (const peer of peers) {
    if (peer.selection?.type !== 'node') continue
    byNode.set(peer.selection.id, [
      ...(byNode.get(peer.selection.id) ?? []),
      peer,
    ])
  }
  return byNode
}

export type TreeDirection = 'LR' | 'TB'

/** The room's `layout` key for a node dragged in one layout direction. */
export const layoutKey = (direction: TreeDirection, id: string): string =>
  `${direction}:${id}`

/* ponytail: a dragged node keeps its place; every other node keeps the
   computed one, so a node nobody has moved can land on one somebody has.
   Upgrade: lay out only the unplaced nodes around the placed ones. */
export const placeNodes = <T extends { id: string; position: NodePosition }>(
  computed: T[],
  layout: Record<string, NodePosition>,
  direction: TreeDirection,
): T[] =>
  computed.map((node) => {
    const placed = layout[layoutKey(direction, node.id)]
    return placed ? { ...node, position: placed } : node
  })

/** Where peers are holding nodes right now, on a canvas laid out as `focus`
 *  is: a position in flow space only means the same to someone laid out the
 *  same way. Peers arrive from an unchecked ephemeral store, so a malformed
 *  position is dropped rather than moving a node to NaN. */
export const peerDrags = (
  peers: PresencePeer[],
  focus: string,
): Map<string, NodePosition> => {
  const held = new Map<string, NodePosition>()
  for (const peer of peers) {
    if (peer.focus !== focus) continue
    for (const [id, position] of Object.entries(peer.dragging ?? {}))
      if (Number.isFinite(position?.x) && Number.isFinite(position?.y))
        held.set(id, position)
  }
  return held
}

/** The paths peers are drawing or moving on a canvas laid out as `focus` is,
 *  with the flow-space point each one's free end is at (their cursor). A
 *  malformed entry is dropped, as in {@link peerDrags}. */
export const peerConnections = (
  peers: PresencePeer[],
  focus: string,
): { peer: PresencePeer; source: string; path: YesNo; end: NodePosition }[] =>
  peers.flatMap((peer) => {
    const { connecting, cursor } = peer
    if (peer.focus !== focus || !connecting || !cursor) return []
    const { source, path } = connecting
    if (typeof source !== 'string' || (path !== 'yes' && path !== 'no'))
      return []
    if (!Number.isFinite(cursor.x) || !Number.isFinite(cursor.y)) return []
    return [{ peer, source, path, end: cursor }]
  })

/** Where a line meets a node's handle, and the handle's centre, in flow space. */
export const handleAnchor = (
  node: Pick<GraphNode, 'computedPosition'>,
  handle: HandleElement,
) => {
  const left = node.computedPosition.x + handle.x
  const top = node.computedPosition.y + handle.y
  const centre = { x: left + handle.width / 2, y: top + handle.height / 2 }
  const edge = {
    [Position.Left]: { x: left, y: centre.y },
    [Position.Right]: { x: left + handle.width, y: centre.y },
    [Position.Top]: { x: centre.x, y: top },
    [Position.Bottom]: { x: centre.x, y: top + handle.height },
  }[handle.position]
  return { centre, edge }
}

/* An edit re-runs the computed layout, which would move every node nobody
   has dragged. These are the nodes the edit keeps that would move, pinned
   where they sit now, so an edit moves nothing but what it adds.
   ponytail: held in both directions, so after edits the direction not in
   view flips to its old places plus fresh ones for new nodes; Clean up
   re-lays it out. Upgrade: lay out only the unplaced nodes. */
export const heldPlaces = (
  before: DecisionTree,
  after: DecisionTree,
  layout: Record<string, NodePosition>,
  direction: TreeDirection,
): { id: string; position: NodePosition }[] => {
  const now = new Map(
    layoutPredicateTree(before.nodes, before.insights, direction).nodes.map(
      (node) => [node.id, node.position],
    ),
  )
  return layoutPredicateTree(after.nodes, after.insights, direction)
    .nodes.filter(({ id }) => !layout[layoutKey(direction, id)])
    .flatMap(({ id, position }) => {
      const at = now.get(id)
      return at && (at.x !== position.x || at.y !== position.y)
        ? [{ id, position: at }]
        : []
    })
}

/** `layout` keys for nodes the tree no longer has. */
export const strayLayoutKeys = (
  layout: Record<string, NodePosition>,
  tree: DecisionTree,
): string[] => {
  const ids = new Set([...tree.nodes, ...tree.insights].map(({ id }) => id))
  return Object.keys(layout).filter(
    (key) => !ids.has(key.slice(key.indexOf(':') + 1)),
  )
}

/** Open threads per item id, keyed by comment target: how many unresolved,
 *  un-replied-to threads sit on each node or row. */
export const openThreadsByItem = (
  comments: readonly ResolvedComment[],
): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const comment of comments) {
    const itemId = fieldIdFromCommentTarget(comment.target)
    if (!itemId || comment.parentId || comment.resolved) continue
    counts.set(itemId, (counts.get(itemId) ?? 0) + 1)
  }
  return counts
}

/** The layout positions ids; the entities come from the room, typed by kind. */
export const treeEntities = (
  nodes: readonly PredicateData[],
  insights: readonly PolicyInsight[],
): Map<string, TreeEntity> =>
  new Map<string, TreeEntity>([
    ...nodes.map(
      (node) => [node.id, { kind: 'predicate' as const, node }] as const,
    ),
    ...insights.map(
      (node) => [node.id, { kind: 'insight' as const, node }] as const,
    ),
  ])

export interface FlowNode {
  id: string
  type: 'collab'
  position: NodePosition
  data: CollabTreeNodeData
}

/** Vue Flow's node list: the computed layout, placed where peers or a saved
 *  drag put it, carrying everything a `CollabTreeNode` renders. */
export const flowNodes = (input: {
  nodes: { id: string; position: NodePosition }[]
  layout: Record<string, NodePosition>
  direction: TreeDirection
  entities: Map<string, TreeEntity>
  view: Pick<PolicyDocView, 'nodeLinks' | 'notes'>
  incoming: Set<string>
  outgoing: Set<string>
  selectedId: string | null
  connectable: boolean
  viewers: Map<string, PresencePeer[]>
  openThreads: Map<string, number>
  heldByPeers: Map<string, NodePosition>
}): FlowNode[] =>
  placeNodes(input.nodes, input.layout, input.direction).flatMap((node) => {
    const entity = input.entities.get(node.id)
    if (!entity) return []
    const itemId = nodeItemId(node.id)
    const data: CollabTreeNodeData = {
      ...entity,
      direction: input.direction,
      root: entity.kind === 'predicate' && !input.incoming.has(node.id),
      orphan: !input.incoming.has(node.id) && !input.outgoing.has(node.id),
      selected: input.selectedId === node.id,
      connectable: input.connectable,
      viewers: input.viewers.get(node.id) ?? [],
      counts: {
        fields: input.view.nodeLinks[node.id]?.fields.length ?? 0,
        discussion:
          (input.openThreads.get(itemId) ?? 0) +
          input.view.notes.filter((note) => note.fieldId === itemId).length,
      },
    }
    return [
      {
        id: node.id,
        type: 'collab' as const,
        position: input.heldByPeers.get(node.id) ?? node.position,
        data,
      },
    ]
  })

const edgeIsPath = (
  edge: Pick<Edge, 'source' | 'sourceHandle'>,
  at: { source: string; path: YesNo } | null,
): boolean =>
  at?.source === edge.source && handlePath(edge.sourceHandle) === at.path

/** The layout's edges, classed for the path in hand, the paths peers are
 *  holding, the selected path, and the path just dropped (it settles flat). */
export const decorateEdges = <E extends Pick<Edge, 'source' | 'sourceHandle'>>(
  edges: readonly E[],
  state: {
    moving: { source: string; path: YesNo } | null
    heldPaths: readonly { source: string; path: YesNo }[]
    selectedEdge: { source: string; path: YesNo } | null
    settling: { source: string; path: YesNo } | null
  },
): (E & { class: string })[] =>
  edges.map((edge) => ({
    ...edge,
    class: [
      edgeIsPath(edge, state.moving) ||
      state.heldPaths.some((held) => edgeIsPath(edge, held))
        ? 'collab-tree-edge--moving'
        : edgeIsPath(edge, state.selectedEdge) && 'collab-tree-edge--selected',
      edgeIsPath(edge, state.settling) && 'collab-tree-edge--settling',
    ]
      .filter(Boolean)
      .join(' '),
  }))

/** Where a node added on a path goes: one gap past the path's Yes or No
 *  handle, along the layout, so Yes and No children don't overlap. */
export const besidePath = (
  node: Pick<GraphNode, 'computedPosition'>,
  handle: HandleElement,
  direction: TreeDirection,
): NodePosition => {
  const x = node.computedPosition.x + handle.x + handle.width / 2
  const y = node.computedPosition.y + handle.y + handle.height / 2
  const GAP = 80
  return direction === 'LR'
    ? { x: x + NODE_WIDTH / 2 + GAP, y }
    : { x, y: y + NODE_HEIGHT / 2 + GAP }
}

/* The context menu reads what was right-clicked. */
export type MenuTarget =
  | { kind: 'pane' }
  | { kind: 'node'; id: string }
  | { kind: 'edge'; source: string; path: YesNo }

/** n, then the node's letter, adds it unattached: d a decision, and an
 *  outcome by its type. */
export const ADD_KEY: Record<PolicyInsightType, string> = {
  'Claim Line Valid': 'v',
  'Claim Line Partially Payable': 'p',
  'Not Applicable': 'a',
  'Claim Line Not Payable': 'n',
}

export type TreeMenuAction =
  | { kind: 'open'; id: string; label: string; icon: string }
  | {
      kind: 'add-decision'
      label: string
      icon: string
      kbds?: string[]
      from?: { source: string; path: YesNo }
    }
  | {
      kind: 'add-outcome'
      label: string
      icon: string
      insightType: PolicyInsightType
      kbds?: string[]
      from?: { source: string; path: YesNo }
    }
  | {
      kind: 'add-outcome-menu' | 'add-on-path-menu'
      label: string
      icon: string
      children: TreeMenuAction[]
    }
  | {
      kind: 'remove-path'
      label: string
      icon: string
      source: string
      path: YesNo
    }
  | {
      kind: 'remove-node'
      label: string
      icon: string
      id: string
      color: 'error'
      kbds: string[]
    }
  | { kind: 'fit-view'; label: string; icon: string }
  | { kind: 'toggle-direction'; label: string; icon: string }
  | { kind: 'clean-up'; label: string; icon: string; kbds: string[] }

const PATH_LABEL = { yes: 'Yes', no: 'No' } as const

const outcomeActions = (from?: {
  source: string
  path: YesNo
}): TreeMenuAction[] =>
  OUTCOME_TYPES.map(({ value, label, icon }) => ({
    kind: 'add-outcome',
    label,
    icon,
    insightType: value,
    kbds: from ? undefined : ['n', ADD_KEY[value]],
    from,
  }))

const removePathAction = (source: string, path: YesNo): TreeMenuAction => ({
  kind: 'remove-path',
  label: `Remove ${PATH_LABEL[path]} path`,
  icon: 'i-ph-scissors',
  source,
  path,
})

const paneActions = (input: {
  treeEditable: boolean
  placedHere: boolean
  direction: TreeDirection
}): TreeMenuAction[][] => {
  const groups: TreeMenuAction[][] = [
    input.treeEditable
      ? [
          {
            kind: 'add-decision',
            label: 'Add decision',
            icon: 'i-ph-git-fork',
            kbds: ['n', 'd'],
          },
          {
            kind: 'add-outcome-menu',
            label: 'Add outcome',
            icon: 'i-ph-flag',
            children: outcomeActions(),
          },
        ]
      : [],
    [
      {
        kind: 'fit-view',
        label: 'Fit to view',
        icon: 'i-ph-arrows-out',
      },
      {
        kind: 'toggle-direction',
        label:
          input.direction === 'TB'
            ? 'Lay out horizontally'
            : 'Lay out vertically',
        icon: 'i-ph-arrows-clockwise',
      },
      ...(input.treeEditable && input.placedHere
        ? [
            {
              kind: 'clean-up' as const,
              label: 'Clean up',
              icon: 'i-ph-magic-wand',
              kbds: ['shift', 'l'],
            },
          ]
        : []),
    ],
  ]
  return groups.filter((group) => group.length)
}

const nodeActions = (
  id: string,
  entity: TreeEntity | undefined,
  input: {
    treeEditable: boolean
    placedHere: boolean
    direction: TreeDirection
  },
): TreeMenuAction[][] => {
  if (!entity) return paneActions(input)
  const opening: TreeMenuAction[] = [
    { kind: 'open', id, label: 'Open', icon: 'i-ph-sidebar-simple' },
  ]
  if (!input.treeEditable) return [opening]
  const paths =
    entity.kind === 'predicate'
      ? (['yes', 'no'] as const).map((path): TreeMenuAction => {
          const target =
            path === 'yes' ? entity.node.yesPath : entity.node.noPath
          if (target) return removePathAction(id, path)
          const from = { source: id, path }
          return {
            kind: 'add-on-path-menu',
            label: `Add on ${PATH_LABEL[path]}`,
            icon: 'i-ph-plus',
            children: [
              {
                kind: 'add-decision',
                label: 'Decision',
                icon: 'i-ph-git-fork',
                from,
              },
              {
                kind: 'add-outcome-menu',
                label: 'Outcome',
                icon: 'i-ph-flag',
                children: outcomeActions(from),
              },
            ],
          }
        })
      : []
  return [
    opening,
    ...(paths.length ? [paths] : []),
    [
      {
        kind: 'remove-node',
        label:
          entity.kind === 'predicate' ? 'Delete decision' : 'Delete outcome',
        icon: 'i-ph-trash',
        color: 'error',
        kbds: ['delete'],
        id,
      },
    ],
  ]
}

/** The context menu's items for whatever was right-clicked: the pane, a node
 *  or a path. Pure descriptors, grouped as the menu divides them; the SFC
 *  maps each `kind` to its handler and to a `ContextMenuItem`. */
export const treeMenuActions = (input: {
  target: MenuTarget
  treeEditable: boolean
  placedHere: boolean
  direction: TreeDirection
  entities: Map<string, TreeEntity>
}): TreeMenuAction[][] => {
  const { target } = input
  if (target.kind === 'node')
    return nodeActions(target.id, input.entities.get(target.id), input)
  if (target.kind === 'edge' && input.treeEditable)
    return [[removePathAction(target.source, target.path)]]
  return paneActions(input)
}

/** Where a line in hand meets the node it has locked onto: the socket it
 *  will plug into, and the handle's own position for the smooth-step path. */
export const socketGeometry = (
  node: Pick<GraphNode, 'computedPosition'>,
  handle: HandleElement,
): { edge: { x: number; y: number }; centre: { x: number; y: number }; position: Position } => ({
  ...handleAnchor(node, handle),
  position: handle.position,
})

/** The locked plug's fill: the node's colour if it is one, grey while loose. */
export const PLUG_FILL = {
  'Claim Line Valid': 'fill-success-500 dark:fill-success-200',
  'Claim Line Not Payable': 'fill-error-600 dark:fill-error-200',
  'Claim Line Partially Payable': 'fill-error-600 dark:fill-error-200',
  'Not Applicable': 'fill-warning-500 dark:fill-warning-200',
} satisfies Record<PolicyInsightType, string>
export const plugStyle = (
  target: { kind: 'predicate' } | { kind: 'insight'; insightType?: PolicyInsightType } | undefined,
): string => {
  if (!target) return 'fill-neutral-400 dark:fill-neutral-500'
  if (target.kind === 'predicate') return 'fill-blue-800 dark:fill-blue-100'
  return target.insightType
    ? PLUG_FILL[target.insightType]
    : 'fill-(--ui-text)'
}

/** "Nowhere yet" stands for an empty path: the select refuses an empty item
 *  value. */
export const NOWHERE = ':nowhere'

/** Every other node's label is a place a path can lead. */
export const pathOptions = (
  nodes: readonly PredicateData[],
  insights: readonly PolicyInsight[],
  currentId: string,
): { label: string; value: string }[] => [
  { label: 'Nowhere yet', value: NOWHERE },
  ...nodes
    .filter((node) => node.id !== currentId)
    .map((node) => ({ label: `Decision ${node.label}`, value: node.label })),
  ...insights.map((entry) => ({
    label: `${entry.label} (outcome)`,
    value: entry.label,
  })),
]


/** The version history, grouped as Figma lists it: a named save on its own,
 *  the editing sessions between named saves bundled together. */
export type TreeVersionGroup =
  | { kind: 'named'; key: string; version: TreeVersion }
  | { kind: 'sessions'; key: string; versions: TreeVersion[] }
export const groupTreeVersions = (
  versions: readonly TreeVersion[],
): TreeVersionGroup[] =>
  versions.reduce<TreeVersionGroup[]>((all, version) => {
    const last = all.at(-1)
    if (version.name) all.push({ kind: 'named', key: version.id, version })
    else if (last?.kind === 'sessions') last.versions.push(version)
    else all.push({ kind: 'sessions', key: version.id, versions: [version] })
    return all
  }, [])
